import { ChevronRight, Loader2 } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "wouter";
import { KeyboardShortcutsSettings } from "../../components/keyboard/KeyboardShortcutsSettings.tsx";
import { KeyboardShortcutsToggle } from "../../components/keyboard/KeyboardShortcutsToggle.tsx";
import { SendInBackgroundToggle } from "../../components/messages/Omnibox/SendInBackgroundToggle.tsx";
import {
  MainContentWrapper,
  SiteHeaderTitle,
  ViewWrapper,
} from "../../components/layout/ViewWrapper.tsx";
import {
  panelHeaderControlClass,
  SplitPanelBackButton,
  SplitPanelHeader,
} from "../../components/layout/SplitPanel/SplitPanelHeader.tsx";
import { HeaderUtilitySlot } from "@/components/layout/HeaderUtilitySlot.tsx";
import { MobileNavMenu, type MobileDestination } from "@/components/layout/MobileNavMenu.tsx";
import { PIVOT_PATHS } from "@/components/layout/pivotRoutes.ts";
import { rememberThreadListOpen, storeMobileNavTab } from "@/components/layout/mobileNavTab.ts";
import { usePreferencesRequest } from "../../components/preferences/usePreferences.tsx";
import { Button } from "../../components/ui/button.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs.tsx";
import { useMediaQuery } from "../../hooks/useMediaQuery.tsx";
import { panelHeaderTitleClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { cn } from "../../lib/utils.ts";
import { AccountsPanel } from "./AccountsPanel.tsx";
import { BillingPanel } from "./Billing.tsx";
import { ReferralsPanel } from "./Referrals.tsx";
import { AssistantContactCard } from "./AssistantContactCard.tsx";
import { AssistantNameCard } from "./AssistantNameCard.tsx";
import { WhatsappAssistantCard } from "./WhatsappAssistantCard.tsx";
import { CommunicationsInstructions } from "./CommunicationsInstructions.tsx";
import { ConnectorsGalleryPanel } from "../ConnectorsGallery.tsx";
import { CustomInstructions } from "./CustomInstructions.tsx";
import { EmailDraftsPreferences } from "./EmailDrafts.tsx";
import { LocationPreferences } from "./Location.tsx";
import { Computers } from "./Computers.tsx";
import { McpSetup } from "./McpSetup.tsx";
import { NotificationSettings } from "./Notifications.tsx";
import { SchedulingPreferences } from "./Scheduling.tsx";
import { RoutinesFlow } from "../Routines.tsx";
import { SkillsFlow } from "../Skills.tsx";
import {
  panelDescriptionClass,
  SettingsHeaderSlotContext,
  SettingsPanelHeader,
} from "./SettingsPanelHeader.tsx";
import { SettingsRow } from "./SettingsRow.tsx";
import { SettingsNavGuardProvider, type SettingsNavGuard } from "./settingsNavGuard.tsx";
import { MemoriesPanel } from "./Memories.tsx";
import { LibraryPanel } from "../Library.tsx";
import { ContactsPanel } from "../Contacts.tsx";
import {
  useMobileSettingsRows,
  SETTINGS_TAB_ICONS,
  usePromotedTabs,
  useEnabledSettingsTabs,
  useSettingsMenuTabs,
  type SettingsTabValue,
  settingsTabLabel,
  listQuerySuffix,
  settingsTabPath,
} from "./settingsNav.ts";

type ActionRowProps = {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  onClick: () => void;
  destructive?: boolean;
  loading?: boolean;
  trailingIcon?: React.ReactNode;
  statsigName: string;
};

const ActionRow = ({
  icon,
  title,
  description,
  onClick,
  destructive,
  loading,
  trailingIcon,
  statsigName,
}: ActionRowProps) => (
  // Render via the design-system <Button> for shared statsig tracking, focus
  // ring, and disabled-state behavior. `variant="blank"` + `size="fit"` strip
  // the default pill chrome (bg, padding, height, rounding); the overrides below
  // restore the full-width row layout with a single bottom divider.
  <Button
    type="button"
    variant="blank"
    size="fit"
    onClick={onClick}
    disabled={loading}
    data-statsig-name={statsigName}
    className={cn(
      "w-full flex items-center justify-between gap-4 py-4 text-left whitespace-normal rounded-none border-0 border-b border-border last:border-b-0 hover:bg-accent/30 disabled:cursor-not-allowed disabled:opacity-70",
      destructive && "text-destructive",
    )}
  >
    <div className="flex items-center gap-3 flex-1 min-w-0">
      {icon}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {description && <div className="text-sm text-muted-foreground mt-0.5">{description}</div>}
      </div>
    </div>
    {loading ? (
      <Loader2 className="size-5 shrink-0 animate-spin" strokeWidth={2.5} />
    ) : (
      (trailingIcon ?? <ChevronRight className="size-5 shrink-0" strokeWidth={2.5} />)
    )}
  </Button>
);

/**
 * Displays a comprehensive user preferences page organized by tabs.
 * Each preference component handles its own update logic and state management.
 * This component is responsible only for layout and data fetching.
 * Supports deep linking to specific tabs via ?tab=<tabName> query parameter.
 */
export const Settings = () => {
  const { data: preferencesData, isLoading, error } = usePreferencesRequest();
  const params = useParams<{ tab?: string; subId?: string }>();
  const [, setLocation] = useLocation();
  // A Settings panel (the routine or skill editor / create form) can register an
  // unsaved-changes guard here; `handleTabChange` routes tab switches through it
  // so edits aren't lost.
  const navGuardRef = useRef<SettingsNavGuard | null>(null);
  const navGuardValue = useMemo(
    () => ({
      setGuard: (guard: SettingsNavGuard | null) => {
        navGuardRef.current = guard;
      },
    }),
    [],
  );
  const preferences = preferencesData?.preferences ?? {};
  const settingsTabs = useEnabledSettingsTabs();
  // What the rail lists is narrower than what this screen renders. Search and
  // Connectors are always named by the nav list, and Routines and Skills join
  // them outside the tasks rollout. They keep their panels and their routes, so
  // a saved link still opens one here, but the rail leaves them to the canvas
  // views that own them rather than offering a second way in.
  const railTabs = useSettingsMenuTabs();
  const promotedTabs = usePromotedTabs();
  const mobileSettingsRows = useMobileSettingsRows();

  const matchedTab = settingsTabs.find((tab) => tab.value === params.tab);
  // Accounts is the fallback for bare `/settings` and unrecognized tabs.
  const activeTab: SettingsTabValue = matchedTab?.value ?? "account";

  const isDesktop = useMediaQuery("md");
  // On mobile, bare `/settings` (or an unrecognized tab) renders the drill-in
  // list; a recognized tab renders its panel full screen. Desktop keeps the
  // sidebar rail with the Connectors fallback for both cases.
  const showMobileList = !isDesktop && !matchedTab;

  // Desktop's default tab (Connectors) lives at bare `/settings`, the same URL
  // that means "the drill-in list" on mobile. Crossing the breakpoint with a
  // panel open must not swap it for the list, so the crossing pins the URL to
  // the tab's explicit path. A replace, because the two URLs are the same
  // place — back should not step through the alias.
  const wasDesktop = useRef(isDesktop);
  useEffect(() => {
    const crossedToMobile = wasDesktop.current && !isDesktop;
    wasDesktop.current = isDesktop;
    if (crossedToMobile && !matchedTab) {
      setLocation(`/settings/${activeTab}`, { replace: true });
    }
  }, [isDesktop, matchedTab, activeTab, setLocation]);

  // Only the routine/skill editor / create form registers a guard, and only
  // while it has unsaved edits; otherwise the navigation runs immediately.
  const runGuarded = useCallback((proceed: () => void) => {
    const guard = navGuardRef.current;
    if (guard) {
      guard(proceed);
    } else {
      proceed();
    }
  }, []);

  const handleTabChange = useCallback(
    (value: string) => {
      runGuarded(() => setLocation(settingsTabPath(value), { replace: true }));
    },
    [runGuarded, setLocation],
  );

  // Rows push (rather than replace) so browser back mirrors the header's
  // panel-to-list hop. The path is explicit rather than via settingsTabPath:
  // that returns bare `/settings` for the default tab, which on mobile IS this
  // list, so the default tab's own row would navigate to itself and never open.
  const handleRowSelect = useCallback(
    (value: string) => {
      runGuarded(() => setLocation(`/settings/${value}`));
    },
    [runGuarded, setLocation],
  );

  const handleTakeTour = useCallback(() => {
    setLocation("/?nux=true");
  }, [setLocation]);

  // Leaving by the nav menu. Every destination but the thread list is a path, so
  // most of this is a navigation; the list is the one the shell holds in state,
  // which is why it has to be asked for before the shell mounts and reads it.
  const handleNavSelect = useCallback(
    (destination: MobileDestination) => {
      runGuarded(() => {
        const wantsThreadList = destination === "threads";
        rememberThreadListOpen(wantsThreadList);
        if (wantsThreadList || destination === "calendar") {
          storeMobileNavTab(wantsThreadList ? "chats" : "calendar");
        }
        setLocation(wantsThreadList ? PIVOT_PATHS.newchat : PIVOT_PATHS[destination]);
      });
    },
    [runGuarded, setLocation],
  );

  // Back navigates to this screen's parent rather than walking raw history: a
  // `:subId` child page returns to its tab's list, and a tab exits Settings to
  // home. `history.back()` — the default — instead lands wherever the user
  // happened to arrive from, which for a child page is often the very page they
  // just left, since the in-panel back button replaces rather than pops.
  // Returning `false` tells the header we performed the navigation ourselves.
  const handleBack = useCallback((): boolean => {
    runGuarded(() => {
      if (params.subId) {
        // Replace, so backing out of a child collapses its entry and one more
        // back press leaves Settings instead of re-entering the child. The
        // query rides along: it holds the list's own state (Contacts' search
        // and filters), which the child inherited on the way in, so dropping
        // it here would return to a different list than the one departed —
        // the chrome chevron is the only way back on mobile, and it must land
        // where the desktop breadcrumb does.
        //
        // Built through the same rule the breadcrumb uses, so a param that
        // means "a contact is open" can't ride back out to the list and
        // reopen the child we are leaving.
        const listQuery = listQuerySuffix(new URLSearchParams(window.location.search));
        setLocation(`${settingsTabPath(activeTab)}${listQuery}`, { replace: true });
      } else if (!isDesktop && matchedTab && !promotedTabs.has(activeTab)) {
        // A drilled-in unpromoted tab on mobile returns to the list; promoted
        // tabs are reached from the nav sheet, so they exit to home like the
        // list itself and every desktop case.
        setLocation("/settings", { replace: true });
      } else {
        setLocation("/");
      }
    });
    return false;
  }, [params.subId, activeTab, isDesktop, matchedTab, promotedTabs, runGuarded, setLocation]);

  // The drilled-in mobile header carries the active panel's actions
  // (SettingsPanelHeader portals them here as icon buttons, nav-bar style).
  // Held as state rather than a ref so the panel re-renders and mounts its
  // portal once the slot element exists.
  const [mobileHeaderSlot, setMobileHeaderSlot] = useState<HTMLElement | null>(null);
  // Child screens (a routine's detail page, say) push their own title into the
  // chrome header through this; top-level panels push the tab label, which is
  // what the header would show anyway.
  const [panelTitle, setPanelTitle] = useState<string | null>(null);
  // While a panel's nav-bar search mode is active, its input takes the title's
  // place in the header (the search slot below renders instead of the title).
  const [searchActive, setSearchActive] = useState(false);
  const [mobileSearchSlot, setMobileSearchSlot] = useState<HTMLElement | null>(null);
  // Desktop: the active panel's whole header row (title/breadcrumb + tools)
  // portals up into the shared bar, beside the Settings title, so the pages
  // don't each spend a row on it.
  const [desktopHeaderSlot, setDesktopHeaderSlot] = useState<HTMLElement | null>(null);
  // Mobile: sits right after the drilled-in title, so a child screen's return
  // link can read as an aside to it instead of taking a line of its own.
  const [mobileTitleSlot, setMobileTitleSlot] = useState<HTMLElement | null>(null);
  const headerSlotValue = useMemo(
    () => ({
      hasChrome: true,
      slotEl: mobileHeaderSlot,
      searchSlotEl: mobileSearchSlot,
      titleSlotEl: mobileTitleSlot,
      desktopSlotEl: desktopHeaderSlot,
      setTitleOverride: setPanelTitle,
      setSearchActive,
    }),
    [mobileHeaderSlot, mobileSearchSlot, mobileTitleSlot, desktopHeaderSlot],
  );

  // "A panel is drilled in on mobile" — the state the chrome header's title,
  // slots and search takeover all key off.
  const isDrilledInMobile = !isDesktop && !!matchedTab;
  const showMobileSearchBar = isDrilledInMobile && searchActive;

  // Every tab and its `:subId` children share this one header, so the whole tree
  // reads the same way. Which control it carries depends on the level: a chevron
  // where there is a parent inside Settings to return to, and nothing at the top
  // of the tree, where leaving is the nav menu's job.
  //
  // Built on the split-panel header, the same tree the thread detail uses, so
  // the chevron scale, gutters, title type, and header height cannot drift
  // from that convention — including its viewport-based height, which keeps
  // the bar from resizing as the *container* narrows.
  const headerContent = (
    <SplitPanelHeader>
      {/* Only where it is going somewhere within Settings. On a drilled-in tab
          or one of its children the chevron is this screen's own navigation —
          the list, or the tab a child was opened from — and on a phone it is the
          only way back up. At the top of the tree it meant nothing but "leave",
          which is what the nav menu at the foot of the screen is for, and a
          second way out beside it read as a level that was not there. */}
      {showMobileList ? null : (
        <SplitPanelBackButton
          onBack={handleBack}
          className={cn(panelHeaderControlClass, "[&_svg]:size-5")}
        />
      )}
      {showMobileSearchBar ? (
        <div ref={setMobileSearchSlot} className="min-w-0 flex-1" />
      ) : (
        // `pr-4` and `@3xl:-ml-1` follow ThreadDetailPanel's title container:
        // the former keeps a truncated title off the controls, the latter
        // cancels the header row's gap step so the title doesn't jog sideways
        // at that width.
        <div className="flex min-w-0 flex-1 items-center pr-4 @3xl:-ml-1">
          {/* Mobile has no tab strip, so the drilled-in title is the only
              indicator of which section is open; desktop keeps "Settings"
              since its rail shows the active tab. The treatment comes from the
              shared panel title rather than a copy of its values, so a drilled-in
              settings page reads as the same kind of page as every other one —
              which on a phone means the display face the thread list sets. */}
          <SiteHeaderTitle className={cn("block pr-0", panelHeaderTitleClass)}>
            {isDrilledInMobile ? (panelTitle ?? settingsTabLabel(activeTab)) : "Settings"}
          </SiteHeaderTitle>
          {/* A child screen's return link portals in here so it reads as an
              aside to the title rather than claiming a second line. Empty and
              zero-width until a panel fills it.

              shrink-0 leaves the title as the row's only giver. Shrinking this
              instead would narrow the slot around a link that does not clip,
              spilling it across the nav-bar icons for any long panel title. */}
          {isDrilledInMobile ? (
            <div ref={setMobileTitleSlot} className="flex shrink-0 items-center" />
          ) : null}
        </div>
      )}
      {/* One right-aligned group rather than two siblings each claiming
          `ml-auto`, which splits the free space between them and leaves the
          actions stranded mid-row instead of on the gutter.

          -mr-2 mirrors the back button's -ml-2: both pull the control box past
          the gutter so the glyph, not the invisible box, lines up with the
          panel inset. It sits on the group so whatever ends the row gets it;
          the utilities cancel it for themselves, since the bug button is filled
          and its box, not its mark, reads as the edge. gap-0.5 is the
          panel-header control-group convention. */}
      <div className="ml-auto -mr-2 flex shrink-0 items-center gap-0.5">
        {isDrilledInMobile && (
          <div ref={setMobileHeaderSlot} className="flex shrink-0 items-center gap-0.5" />
        )}
        <HeaderUtilitySlot className="mr-2" />
      </div>
    </SplitPanelHeader>
  );

  if (isLoading) {
    return (
      <ViewWrapper headerContent={headerContent}>
        <MainContentWrapper columns={14} className="flex-1 min-w-0">
          <div className="flex items-center justify-center h-64">
            <div className="text-muted-foreground">Loading settings...</div>
          </div>
        </MainContentWrapper>
      </ViewWrapper>
    );
  }

  // Don't show error state for aborted requests (common during navigation/remounting)
  const isAbortError =
    error &&
    (("code" in error && error.code === "ECONNABORTED") ||
      ("name" in error && error.name === "AbortError") ||
      ("message" in error &&
        typeof error.message === "string" &&
        error.message.includes("aborted")));

  if (error && !isAbortError && !isLoading) {
    return (
      <ViewWrapper headerContent={headerContent}>
        <MainContentWrapper columns={14} className="flex-1 min-w-0">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-destructive mb-2">Failed to load settings</div>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                data-statsig-name="settings-error-retry"
              >
                Try again
              </Button>
            </div>
          </div>
        </MainContentWrapper>
      </ViewWrapper>
    );
  }

  // One body per tab, rendered through the same TabsContent positions on both
  // breakpoints so crossing 768px mid-edit never remounts a guarded editor
  // and drops its unsaved state.
  const panelBodies: Record<SettingsTabValue, ReactNode> = {
    // The gallery renders its own SettingsPanelHeader: search, filter, and
    // Sync all live in the header row, so the panel that owns their state
    // owns the header too.
    connectors: <ConnectorsGalleryPanel />,
    account: <AccountsPanel />,
    billing: <BillingPanel />,
    referrals: <ReferralsPanel />,
    scheduling: <SchedulingPreferences />,
    location: (
      <LocationPreferences
        timezonePreference={preferences.location?.timezonePreference ?? "current"}
      />
    ),
    notifications: (
      <div className="w-full pb-12">
        <SettingsPanelHeader title="Notifications" />
        <p className={panelDescriptionClass}>
          How and where Okthx notifies you: push, badge, email, and SMS.
        </p>
        <NotificationSettings />
      </div>
    ),
    assistant: (
      <>
        {/* Header outside the space-y root so the gap under the title stays
            the standard mb-6 instead of the section rhythm. */}
        <SettingsPanelHeader title="Assistant" />
        <p className={panelDescriptionClass}>
          Your assistant's contact details, and standing instructions for how it works.
        </p>
        <div className="w-full space-y-8 pb-12">
          <AssistantNameCard />
          <AssistantContactCard />
          <WhatsappAssistantCard />
          <div>
            <h2 className="text-base font-semibold mb-2">Custom instructions</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Standing guidance the assistant will follow: tone, format, and personal preferences.
            </p>
            <CustomInstructions customInstructions={preferences.agent?.customInstructions ?? ""} />
          </div>
          <div>
            <h2 className="text-base font-semibold mb-2">Communications instructions</h2>
            <p className="text-sm text-muted-foreground mb-4">
              How the assistant communicates with <em>other people</em> on your behalf: emails,
              meeting invitations, and other outbound messages.
            </p>
            <CommunicationsInstructions
              communicationsInstructions={preferences.communications?.customInstructions ?? ""}
            />
          </div>
          <div>
            <h2 className="text-base font-semibold mb-2">Email drafts</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Where drafts the assistant writes for you show up, and how they sign off.
            </p>
            <EmailDraftsPreferences />
          </div>
        </div>
      </>
    ),
    routines: (
      <SettingsNavGuardProvider value={navGuardValue}>
        <RoutinesFlow />
      </SettingsNavGuardProvider>
    ),
    memories: <MemoriesPanel />,
    skills: (
      <SettingsNavGuardProvider value={navGuardValue}>
        <SkillsFlow />
      </SettingsNavGuardProvider>
    ),
    library: <LibraryPanel />,
    contacts: <ContactsPanel />,
    mcp: <McpSetup />,
    computers: <Computers />,
    app: (
      <div className="w-full pb-12">
        <SettingsPanelHeader title="General" />
        <p className={panelDescriptionClass}>App-wide preferences and shortcuts.</p>
        <SettingsRow
          title="Keyboard shortcuts"
          description="Press ? to see all available shortcuts"
          control={<KeyboardShortcutsToggle />}
        />
        <SettingsRow
          title="Send in background"
          description="Stay where you are when a message starts a new thread. Cmd+Enter opens it instead."
          control={<SendInBackgroundToggle />}
        />
        <ActionRow
          title="Take a tour of Okthx"
          onClick={handleTakeTour}
          statsigName="settings-take-tour"
        />
        <KeyboardShortcutsSettings />
      </div>
    ),
  };

  return (
    <SettingsHeaderSlotContext.Provider value={headerSlotValue}>
      {/* Desktop draws its own shared bar inside the content wrapper below —
          that is what lets the panel header align exactly with the content
          column at every width. Mobile keeps the chrome nav bar. */}
      <ViewWrapper headerContent={isDesktop ? undefined : headerContent}>
        <MainContentWrapper
          columns={14}
          // Clears the last row from under the nav menu floating over the foot
          // of the screen, the same room the canvas body leaves for it. Only
          // where that menu is: a drilled-in tab has the header's chevron
          // instead and needs no space kept at the bottom.
          className={cn("flex-1 min-w-0", showMobileList && "pb-28")}
        >
          {isDesktop && (
            // One shared bar: chevron + "Settings" over the rail's column,
            // then the active panel's header row over the content column.
            // The leading zone mirrors the body grid (w-48 + gap-8), which is
            // what keeps the two columns in register; sticky like the rail so
            // scrolling a long page keeps the tools at hand.
            <div className="sticky top-0 z-20 flex h-24 items-center bg-background">
              <div className="flex w-48 shrink-0 items-center">
                <SplitPanelBackButton
                  onBack={handleBack}
                  className={cn(panelHeaderControlClass, "[&_svg]:size-5")}
                />
                <SiteHeaderTitle className="block truncate pr-0 text-base @5xl:text-base @5xl:tracking-normal font-medium">
                  Settings
                </SiteHeaderTitle>
              </div>
              <div ref={setDesktopHeaderSlot} className="ml-8 min-w-0 flex-1" />
              {/* This bar replaces the screen header from `md`, which is where
                  `headerContent` stops being rendered — so without this the
                  utilities have no slot on the widest Settings screen and fall
                  back onto the corner this bar already occupies. */}
              <HeaderUtilitySlot />
            </div>
          )}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <div className="flex flex-col md:flex-row md:gap-8">
              {/* md:top-24 keeps the rail pinned below the shared bar. */}
              <div className="md:w-48 md:shrink-0 sticky top-0 md:top-24 z-10 bg-background md:self-start">
                {/* On mobile the rail collapses to sr-only rather than
                    unmounting: drilled-in tabpanels keep their labeling
                    triggers, and the tree keeps one shape across the
                    breakpoint so a resize can't remount a guarded editor. */}
                <TabsList
                  variant="sidebar"
                  size="sidebar"
                  aria-label="Settings sections"
                  className={isDesktop ? undefined : "sr-only"}
                >
                  {/* A trigger for every tab this screen can render, with the
                      ones the rail no longer offers hidden rather than dropped.
                      Radix pairs a panel to its trigger by id, so rendering the
                      panels from one list and the triggers from a shorter one
                      left `/settings/skills` labelled by a trigger that did not
                      exist, and its tab absent from the roving-focus ring.
                      `hidden` keeps them out of the rail and out of the tab
                      order while the pairing survives. */}
                  {settingsTabs.map((tab) => {
                    const Icon = SETTINGS_TAB_ICONS[tab.value];
                    const onRail = railTabs.some((railTab) => railTab.value === tab.value);
                    return (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        variant="sidebar"
                        size="sidebar"
                        className="cursor-pointer"
                        hidden={!onRail}
                        // Keyboard focus must never land in the sr-only rail.
                        tabIndex={isDesktop && onRail ? 0 : -1}
                      >
                        <Icon className="size-4 shrink-0" />
                        {tab.label}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>

              <div className="flex-1 min-w-0">
                {showMobileList ? (
                  <div className="w-full">
                    {mobileSettingsRows
                      .filter((row) => settingsTabs.some((tab) => tab.value === row.value))
                      .map(({ value }) => {
                        const Icon = SETTINGS_TAB_ICONS[value];
                        return (
                          <ActionRow
                            key={value}
                            icon={<Icon className="size-5 shrink-0 text-muted-foreground" />}
                            title={settingsTabLabel(value)}
                            onClick={() => handleRowSelect(value)}
                            statsigName={`settings-list-${value}`}
                          />
                        );
                      })}
                  </div>
                ) : (
                  settingsTabs.map((tab) => (
                    <TabsContent key={tab.value} value={tab.value} className="mt-0">
                      {panelBodies[tab.value]}
                    </TabsContent>
                  ))
                )}
              </div>
            </div>
          </Tabs>
        </MainContentWrapper>
        {/* The same nav menu every other screen carries on a phone, so the top of
            Settings is somewhere you leave the way you leave anything else
            rather than a branch with one exit.

            Only at the top. A tab and its children are drilled into from that
            list, and the header's chevron is the way back up — which is how a
            thread behaves too, and why the shell drops its own bottom bar for
            one. Offering both would put two exits on a screen with one parent.

            Fixed rather than floated inside the content, which scrolls. Guarded
            by the tab's own unsaved-changes check, like every other way out of
            this screen: leaving by the menu must not be the one route that
            skips it. */}
        {showMobileList && (
          <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 px-4 py-6">
            <div className="pointer-events-auto w-fit">
              <MobileNavMenu active={null} onSelect={handleNavSelect} />
            </div>
          </div>
        )}
      </ViewWrapper>
    </SettingsHeaderSlotContext.Provider>
  );
};

export default Settings;
